/* @jsx h */
import { defineComponent, h } from "vue";
import { LemmonModal } from "@lemmonhq/uikit";
import { formatPrice } from "@lemmonhq/orders";
import { useAuth } from "@app/vue/providers/Auth";
import type {
  CheckoutDiscount,
  CheckoutDiscountComponent,
} from "@lemmonhq/types";

// ─── shared helpers (mirror of what CheckoutComponent/index.tsx uses) ─────────

export const getDiscountComponents = (
  discount: CheckoutDiscount | undefined,
): CheckoutDiscountComponent[] => {
  if (!discount) return [];
  if (discount.subcomponents) return discount.subcomponents;
  return [
    {
      source: discount.discount_type_id ? "pre-defined" : "manual",
      value_type: discount.type,
      input_value: discount.value,
      discount_amount: discount.amount,
      category: discount.category,
      discount_type_id: discount.discount_type_id,
      metadata: { cart_items_ids: [] },
    },
  ];
};

export const buildCheckoutDiscount = (
  components: CheckoutDiscountComponent[],
): CheckoutDiscount | undefined => {
  if (components.length === 0) return undefined;
  const amount = components.reduce((s, c) => s + c.discount_amount, 0);
  const first = components[0];
  return {
    type: components.length === 1 ? first.value_type : "fixed-amount",
    category:
      components.length === 1 && first.category ? first.category : "mixed",
    value: components.length === 1 ? first.input_value : amount,
    amount,
    discount_type_id:
      components.length === 1 ? first.discount_type_id : undefined,
    subcomponents: components,
  };
};

// ─── label helpers ─────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  fork: "The Fork",
  employee: "Employee",
  owner: "Owner",
  standard: "Standard",
  mixed: "Mixed",
};

const productsLabel = (c: CheckoutDiscountComponent): string | undefined => {
  const products = c.metadata?.products ?? [];
  if (products.length === 0) return undefined;
  return products
    .map((product) => `${product.quantity} x ${product.name}`)
    .join(", ");
};

const sourceLabel = (c: CheckoutDiscountComponent): string => {
  if (c.source === "order-items") return productsLabel(c) ?? "Item discount";
  if (c.source === "pre-defined")
    return c.category
      ? (CATEGORY_LABELS[c.category] ?? c.category)
      : "Saved discount";
  // manual
  if (c.category && CATEGORY_LABELS[c.category])
    return CATEGORY_LABELS[c.category];
  return "Manual discount";
};

const displayLabel = (c: CheckoutDiscountComponent): string => {
  if (c.source === "order-items") return sourceLabel(c);
  return c.label || sourceLabel(c);
};

const valueLabel = (c: CheckoutDiscountComponent, currency: string): string => {
  if (c.value_type === "percentage") return `${c.input_value}%`;
  return formatPrice(c.discount_amount, true, 2, currency);
};

// ─── component ─────────────────────────────────────────────────────────────────

export type DiscountBreakdownModalProps = {
  discount: CheckoutDiscount;
  onClose: () => void;
  onRemoveComponent: (index: number) => void;
};

export const DiscountBreakdownModal =
  defineComponent<DiscountBreakdownModalProps>({
    name: "DiscountBreakdownModal",
    props: ["discount", "onClose", "onRemoveComponent"],
    setup(props) {
      const auth = useAuth();
      return () => {
        const components = getDiscountComponents(props.discount);
        const currency = auth.user.value?.restaurant.currency || "CHF";

        return (
          <LemmonModal title="Discount breakdown" onClose={props.onClose}>
            <div class="py-4 px-6 space-y-3 min-w-[340px]">
              {components.length === 0 ? (
                <p class="text-center text-sm text-base-content/50 py-6">
                  No discount components.
                </p>
              ) : (
                components.map((component, i) => (
                  <div
                    key={i}
                    class="flex items-center justify-between gap-3 rounded-xl border border-base-content/10 bg-base-100 px-4 py-3"
                  >
                    <div class="min-w-0 flex-1">
                      <div class="text-sm font-medium text-accent truncate">
                        {displayLabel(component)}
                      </div>
                      <div class="mt-0.5 text-xs text-base-content/50">
                        {valueLabel(component, currency)} off
                        {component.source === "order-items" &&
                        component.category ? (
                          <span> · {component.category}</span>
                        ) : null}
                      </div>
                    </div>

                    <div class="flex items-center gap-3 flex-shrink-0">
                      <span class="text-sm font-semibold text-success">
                        {formatPrice(
                          component.discount_amount,
                          true,
                          2,
                          currency,
                        )}
                      </span>
                      <button
                        type="button"
                        class="btn btn-circle btn-xs btn-ghost text-error hover:bg-error/10"
                        onClick={() => props.onRemoveComponent(i)}
                        aria-label="Remove discount"
                      >
                        <span class="material-symbols-outlined text-[16px]">
                          close
                        </span>
                      </button>
                    </div>
                  </div>
                ))
              )}

              {/* Total row */}
              {components.length > 0 && (
                <div class="flex items-center justify-between border-t border-base-content/10 pt-3">
                  <span class="text-sm text-base-content/60">
                    Total discount
                  </span>
                  <span class="text-base font-bold text-success">
                    {formatPrice(props.discount.amount, true, 2, currency)}
                  </span>
                </div>
              )}
            </div>
          </LemmonModal>
        );
      };
    },
  });
